import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Users, Loader2, Sparkles } from "lucide-react";
import { initials } from "@/lib/lifeos";
import { avatarForId } from "@/lib/avatars";

export default function CollabModal({ open, onOpenChange, agents, deptById, onRun }) {
  const [selected, setSelected] = useState([]);
  const [objective, setObjective] = useState("");
  const [running, setRunning] = useState(false);
  const [collab, setCollab] = useState(null);
  const [revealCount, setRevealCount] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSelected([]); setObjective(""); setCollab(null); setRevealCount(0);
    }
    return () => clearInterval(timerRef.current);
  }, [open]);

  const toggle = (id) => setSelected((s) => {
    if (s.includes(id)) return s.filter((x) => x !== id);
    if (s.length >= 4) return s; // cap to keep sync fast
    return [...s, id];
  });

  const run = async () => {
    if (selected.length < 2 || !objective.trim() || running) return;
    setRunning(true);
    setCollab(null);
    setRevealCount(0);
    try {
      const result = await onRun(selected, objective.trim());
      setCollab(result);
      let i = 0;
      timerRef.current = setInterval(() => {
        i += 1;
        setRevealCount(i);
        if (i >= result.transcript.length) clearInterval(timerRef.current);
      }, 1100);
    } catch {
      /* toast handled upstream */
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0b0e14] border border-violet-500/40 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto thin-scroll shadow-[0_0_50px_rgba(167,139,250,0.2)]">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-widest text-violet-300 flex items-center gap-2">
            <Users size={18} /> Meeting Room Sync
          </DialogTitle>
          <DialogDescription className="text-zinc-500 font-mono text-xs">
            Gather 2+ agents to collaborate live on a single objective.
          </DialogDescription>
        </DialogHeader>

        {!collab ? (
          <div className="space-y-4 mt-2">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical mb-1.5">Select Participants ({selected.length}/4)</div>
              <div className="grid sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto thin-scroll pr-1">
                {agents.map((a) => {
                  const on = selected.includes(a.id);
                  const dept = deptById[a.department_id];
                  return (
                    <button
                      key={a.id}
                      data-testid={`collab-agent-${a.id}`}
                      onClick={() => toggle(a.id)}
                      className={`flex items-center gap-2 p-2 rounded-lg border text-left transition-all ${on ? "border-violet-400/50 bg-violet-500/10" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`}
                    >
                      <span className="rounded-full overflow-hidden shrink-0" style={{ width: 30, height: 30, border: `1.5px solid ${a.accent}`, background: "#0b0e14" }}><img src={avatarForId(a.id)} alt="" className="w-full h-full object-cover object-top" /></span>
                      <span className="min-w-0">
                        <span className="block text-sm text-zinc-100 truncate">{a.name}</span>
                        <span className="block text-[10px] font-mono text-zinc-500 truncate">{dept?.name}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical mb-1">Objective</div>
              <Textarea data-testid="collab-objective-input" rows={2} value={objective} onChange={(e) => setObjective(e.target.value)} placeholder="e.g. Plan the Q3 product launch across all teams" className="bg-[#0b0e14] border-white/10 text-sm text-zinc-100 focus-visible:ring-violet-400/40" />
            </div>
            <Button data-testid="collab-start-button" onClick={run} disabled={running || selected.length < 2 || !objective.trim()} className="w-full bg-violet-500 hover:bg-violet-400 text-[#04070c] font-tactical font-bold uppercase tracking-wider">
              {running ? <><Loader2 size={16} className="mr-1 animate-spin" /> Convening...</> : <><Users size={16} className="mr-1" /> Start Sync ({selected.length})</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-3 mt-2" data-testid="collab-transcript">
            <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Live Discussion</div>
            {collab.transcript.slice(0, revealCount).map((t, i) => (
              <div key={i} className="flex items-start gap-2 anim-slidein">
                <span className="rounded-full overflow-hidden shrink-0 mt-0.5" style={{ width: 30, height: 30, border: `1.5px solid ${t.accent}`, background: "#0b0e14" }}><img src={avatarForId(t.agent_id)} alt="" className="w-full h-full object-cover object-top" /></span>
                <div className="rounded-xl px-3 py-2 bg-white/[0.04] border border-white/10">
                  <div className="text-[11px] font-tactical font-bold" style={{ color: t.accent }}>{t.agent_name}</div>
                  <div className="text-sm text-zinc-200">{t.message}</div>
                </div>
              </div>
            ))}
            {revealCount < collab.transcript.length && (
              <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono pl-10"><Loader2 size={13} className="animate-spin" /> agents deliberating...</div>
            )}
            {revealCount >= collab.transcript.length && (
              <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05] p-3 anim-slidein">
                <div className="flex items-center gap-1.5 text-emerald-400 text-[11px] font-tactical font-bold uppercase tracking-wider mb-1"><Sparkles size={13} /> Agreed Plan</div>
                <p className="text-sm text-zinc-100 leading-relaxed">{collab.synthesis}</p>
              </div>
            )}
            <Button onClick={() => { setCollab(null); setSelected([]); setObjective(""); }} variant="ghost" className="w-full text-zinc-400 hover:text-zinc-200">New Sync</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
